// Ficha de parada: nombre, líneas y próximas llegadas en tiempo real (se refresca cada 30 s
// mientras está abierta), con atajos para usarla de origen o destino de una ruta.

import type { Dataset } from "../data";
import type { MapAdapter } from "../map/types";
import { fetchEstimations, type ApiEstimation } from "../live/api";
import { badge, escapeHtml, minutesText, titleCase } from "./common";
import type { DetailView } from "./detail";

const REFRESH_MS = 30_000;

export interface StopViewOptions {
  ds: Dataset;
  adapter: MapAdapter;
  bottomPad: () => number;
  onRoute: (stopIdx: number, role: "origin" | "destination") => void;
  onOpenLine: (line: string) => void;
  // ¿Hay un bus de la línea parado aquí ahora? (del tiempo real del mapa)
  atStop?: (stopId: number, line: string) => boolean;
}

export function stopView(o: StopViewOptions, stopIdx: number): DetailView {
  return {
    mount(el, back) {
      const stop = o.ds.stopsByIdx[stopIdx];
      if (!stop) {
        back();
        return () => {};
      }
      o.adapter.flyTo(stop.ll, 17, o.bottomPad());
      const lineBadges = stop.lines
        .map((l) => {
          const line = o.ds.linesById.get(l);
          return line
            ? `<button type="button" class="chip-line" data-line="${l}">${badge(l, line.color, "s")}</button>`
            : "";
        })
        .join("");
      el.innerHTML = `
        <div class="dhead">
          <button type="button" class="back" aria-label="Volver">‹</button>
          <div><h2>${escapeHtml(titleCase(stop.name || `Parada ${stop.number}`))}</h2>
          <small>Parada ${escapeHtml(stop.number)}</small></div>
        </div>
        <div class="badges">${lineBadges}</div>
        <ul class="arrivals"><li class="muted">Consultando llegadas…</li></ul>
        <div class="actions">
          <button type="button" data-act="from">Salir desde aquí</button>
          <button type="button" data-act="to">Ir aquí</button>
        </div>`;
      el.querySelector(".back")!.addEventListener("click", back);
      el.querySelector(".badges")!.addEventListener("click", (e) => {
        const l = (e.target as HTMLElement).closest<HTMLElement>("[data-line]")?.dataset["line"];
        if (l) o.onOpenLine(l);
      });
      el.querySelector('[data-act="from"]')!.addEventListener("click", () =>
        o.onRoute(stopIdx, "origin"),
      );
      el.querySelector('[data-act="to"]')!.addEventListener("click", () =>
        o.onRoute(stopIdx, "destination"),
      );

      const list = el.querySelector<HTMLUListElement>(".arrivals")!;
      let alive = true;
      const load = async (): Promise<void> => {
        try {
          const ests = await fetchEstimations(stop.id);
          if (alive)
            list.innerHTML = renderArrivals(o.ds, ests, (l) => o.atStop?.(stop.id, l) ?? false);
        } catch {
          if (alive) list.innerHTML = `<li class="muted">Tiempo real no disponible ahora.</li>`;
        }
      };
      void load();
      const timer = window.setInterval(() => {
        if (!document.hidden) void load();
      }, REFRESH_MS);
      return () => {
        alive = false;
        window.clearInterval(timer);
      };
    },
  };
}

// Una fila por línea y destino con el próximo bus y el siguiente.
export function renderArrivals(
  ds: Dataset,
  ests: readonly ApiEstimation[],
  _atStop: (line: string) => boolean = () => false,
): string {
  if (ests.length === 0) return `<li class="muted">Sin buses previstos ahora mismo.</li>`;
  const groups = new Map<string, ApiEstimation[]>();
  for (const e of ests) {
    const k = `${e.line}|${e.destination}`;
    groups.set(k, [...(groups.get(k) ?? []), e]);
  }
  const rows = [...groups.values()]
    .sort((a, b) => a[0]!.minutes - b[0]!.minutes)
    .map((g) => {
      g.sort((a, b) => a.minutes - b.minutes);
      const first = g[0]!;
      const color = ds.linesById.get(first.line)?.color ?? first.color ?? "#555";
      const next = g[1] ? `<small>y ${g[1].minutes} min</small>` : "";
      const occ = first.occupancy
        ? `<small class="occ">${escapeHtml(first.occupancy)}</small>`
        : "";
      return `<li>${badge(first.line, color)}<span class="dest">${escapeHtml(titleCase(first.destination))}${occ}</span>
        <span class="when${first.minutes <= 1 ? " now" : ""}"><b>${minutesText(first.minutes)}</b>${next}</span></li>`;
    })
    .join("");
  const times = ests.map((e) => e.observedAt).filter((at): at is number => at !== undefined);
  if (!times.length) return rows;
  const age = Math.max(0, Math.floor((Date.now() - Math.min(...times)) / 1000));
  return rows + `<li class="muted">Estimaciones del operador · consultadas hace ${age} s</li>`;
}
